import { InformationCircleIcon } from "@heroicons/react/outline";
import { LockClosedIcon } from "@heroicons/react/solid";
import MuiTooltip from "@mui/material/Tooltip";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Read/write adapter for the excluded-layers preference. The component never
 * touches storage directly so the same UI can sit on top of the per-content
 * rail prefs (localStorage keyed by contentId) or the per-user scenaria prefs
 * (localStorage keyed by userId), or anything else.
 */
export interface ContextSelectorPrefs {
  getExcluded: () => string[];
  setExcluded: (excluded: string[]) => void;
}

export interface ContextSelectorProps {
  /**
   * i18n base path. All labels are derived by convention so both consumers
   * stay visually identical:
   *   `${i18nBase}.contextLabel`          (pill, expects {count})
   *   `${i18nBase}.contextExcludedBadge`  (pill, expects {count})
   *   `${i18nBase}.contextEdit`           (pill button)
   *   `${i18nBase}.context.title`         (popover heading)
   *   `${i18nBase}.context.note`          (popover footer)
   *   `${i18nBase}.context.layers.<key>`  (row label)
   *   `${i18nBase}.context.tooltips.<key>`(row info tooltip)
   */
  i18nBase: string;
  /** Always-on layers, rendered as locked rows. */
  mandatoryLayers: readonly string[];
  /** Opt-out layers, rendered as checkboxes. */
  optionalLayers: readonly string[];
  prefs: ContextSelectorPrefs;
  /**
   * Identity of the thing the prefs are scoped to (contentId, userId…).
   * When it changes, the component re-syncs from `prefs`.
   */
  sourceKey: string | number | undefined;
  /** Disables the toggles and forces an empty exclusion set (e.g. no target). */
  disabled?: boolean;
  /** Notified with the full excluded list whenever it changes. */
  onChange?: (excluded: string[]) => void;
  /** Root wrapper classes. Defaults to the rail's spacing. */
  className?: string;
}

const LayerInfoIcon = ({ message }: { message: string }) => (
  <MuiTooltip title={message} arrow placement="left">
    <div className="w-3.5 h-3.5 text-gray-400 cursor-pointer shrink-0 ml-auto">
      <InformationCircleIcon className="w-full h-full" />
    </div>
  </MuiTooltip>
);

/**
 * Pill + popover letting the user opt out of optional context layers.
 * Generalised from the in-graph Scenar IA rail so the scenaria chat reuses
 * the exact same affordance.
 */
export const ContextSelector = ({
  i18nBase,
  mandatoryLayers,
  optionalLayers,
  prefs,
  sourceKey,
  disabled = false,
  onChange,
  className = "relative mb-2",
}: ContextSelectorProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [excluded, setExcluded] = useState<string[]>(() => (disabled ? [] : prefs.getExcluded()));
  const containerRef = useRef<HTMLDivElement>(null);

  // Re-sync whenever the scoped source changes (e.g. user navigates to a
  // different content in the rail). Intentionally keyed on sourceKey only —
  // `prefs` is rebuilt every render by the caller and is read lazily.
  useEffect(() => {
    setExcluded(disabled ? [] : prefs.getExcluded());

  }, [sourceKey, disabled]);

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (isOpen && containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [isOpen]);

  const toggle = useCallback(
    (layer: string) => {
      if (disabled) return;
      setExcluded((prev) => {
        const next = prev.includes(layer) ? prev.filter((l) => l !== layer) : [...prev, layer];
        prefs.setExcluded(next);
        onChange?.(next);
        return next;
      });
    },
    [disabled, prefs, onChange],
  );

  const excludedCount = excluded.length;
  const layerCount = useMemo(
    () => mandatoryLayers.length + optionalLayers.length - excludedCount,
    [mandatoryLayers.length, optionalLayers.length, excludedCount],
  );

  return (
    <div ref={containerRef} className={className}>
      <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-[11px] text-gray-600">
        <span>{t(`${i18nBase}.contextLabel`, { count: layerCount })}</span>
        {excludedCount > 0 && (
          <span className="text-amber-600">
            {t(`${i18nBase}.contextExcludedBadge`, { count: excludedCount })}
          </span>
        )}
        <span className="text-gray-300">·</span>
        <button
          type="button"
          className="text-green-500 hover:text-green-600"
          onClick={() => setIsOpen((v) => !v)}
          aria-expanded={isOpen}
        >
          {t(`${i18nBase}.contextEdit`)}
        </button>
      </div>
      {isOpen && (
        <div className="absolute z-10 mt-1 left-0 w-64 rounded-md border border-gray-200 bg-white shadow-sm p-2">
          <div
            className="text-[11px] text-gray-600"
            role="group"
            aria-label={t(`${i18nBase}.context.title`) as string}
          >
            <div className="font-medium text-gray-700 mb-1.5">{t(`${i18nBase}.context.title`)}</div>
            <ul className="space-y-1">
              {mandatoryLayers.map((layer) => (
                <li key={layer} className="flex items-center gap-2 opacity-60">
                  <LockClosedIcon className="w-3 h-3 text-gray-400 shrink-0" />
                  <span className="flex-1 truncate">
                    {t(`${i18nBase}.context.layers.${layer}`)}
                  </span>
                  <LayerInfoIcon message={t(`${i18nBase}.context.tooltips.${layer}`) as string} />
                </li>
              ))}
              {optionalLayers.map((layer) => {
                const isOn = !excluded.includes(layer);
                return (
                  <li key={layer} className="flex items-center gap-2">
                    <label className="inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="h-3.5 w-3.5 accent-green-500"
                        checked={isOn}
                        disabled={disabled}
                        onChange={() => toggle(layer)}
                        aria-label={t(`${i18nBase}.context.layers.${layer}`) as string}
                      />
                    </label>
                    <span className={`flex-1 truncate ${isOn ? "" : "line-through text-gray-400"}`}>
                      {t(`${i18nBase}.context.layers.${layer}`)}
                    </span>
                    <LayerInfoIcon message={t(`${i18nBase}.context.tooltips.${layer}`) as string} />
                  </li>
                );
              })}
            </ul>
            <div className="text-[10px] text-gray-400 mt-2">{t(`${i18nBase}.context.note`)}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContextSelector;
